import React, { useEffect, useState } from 'react';
import { X, FileText, CheckCircle2, Download, ArrowRight } from 'lucide-react';

/**
 * Gated case-study download modal.
 * Captures name + email + optional company, POSTs to /api/leads/case-study
 * which returns a download URL to open in a new tab.
 */
const CaseStudyModal = ({ open, onClose, study = 'vinci-facilities' }) => {
  const [form, setForm] = useState({ name: '', email: '', company: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [error, setError] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState('');

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    if (open) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setError(null);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API}/api/leads/case-study`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, study, source: 'home-case-study' }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setDownloadUrl(data.download_url || '');
      setStatus('sent');
    } catch (err) {
      setStatus('error');
      setError('Sorry, something went wrong. Email peter@keystone.co and we\u2019ll send it directly.');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="case-study-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-[videra-pop-in_.3s_ease-out]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative bg-slate-950 text-white p-8 flex items-center gap-5">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
            data-testid="case-study-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="w-14 h-14 rounded-lg bg-white flex items-center justify-center flex-shrink-0">
            <img src="/images/clients/vinci-facilities.png" alt="VINCI Facilities" className="max-w-[42px] max-h-[42px] object-contain" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-400 mb-1">Case study</p>
            <h3 className="text-xl font-medium leading-tight">How VINCI Facilities runs live PFI Hard Services on Videra.</h3>
          </div>
        </div>

        <div className="p-8">
          {status === 'sent' ? (
            <div className="text-center py-4" data-testid="case-study-success">
              <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-3">
                <CheckCircle2 className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-medium text-slate-900 mb-2">Sent, check your inbox.</h3>
              <p className="text-sm text-slate-600 mb-5">The full case study is on its way. Grab a preview below.</p>
              <a
                href={downloadUrl || '#'}
                target="_blank" rel="noreferrer"
                data-testid="case-study-download"
                className="inline-flex items-center gap-2 bg-slate-900 text-white px-5 py-3 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors"
              >
                <Download className="w-4 h-4" /> Open the case study
              </a>
            </div>
          ) : (
            <>
              <p className="text-sm text-slate-600 mb-5">
                One page. Real numbers. How a Tier-1 FM contractor connected requirements, projects, evidence and progressive handback readiness across NHS hospital estates.
              </p>
              <form onSubmit={submit} className="space-y-3">
                <input required placeholder="Your name" value={form.name} onChange={update('name')} data-testid="case-study-name" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                <input required type="email" placeholder="Work email" value={form.email} onChange={update('email')} data-testid="case-study-email" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                <input placeholder="Company (optional)" value={form.company} onChange={update('company')} data-testid="case-study-company" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                {error && <p className="text-sm text-red-600" data-testid="case-study-error">{error}</p>}
                <button
                  type="submit"
                  disabled={status === 'sending'}
                  data-testid="case-study-submit"
                  className="w-full bg-slate-900 text-white px-5 py-3 rounded-md text-sm font-medium hover:bg-slate-800 disabled:opacity-60 transition-colors inline-flex items-center justify-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  {status === 'sending' ? 'Sending…' : (<>Send me the case study <ArrowRight className="w-4 h-4" /></>)}
                </button>
                <p className="text-[11px] text-slate-500 text-center">No newsletter. No sales sequence.</p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CaseStudyModal;
